import React from 'react'
import PropTypes from 'prop-types'

import { IfIE } from '@client/components/IfIE'

// Classic Outlook for Windows renders mail with Word, which ignores border-radius, so the
// HTML button shows square corners there. A VML v:roundrect is drawn for Outlook instead and
// the HTML button is hidden from it ("bulletproof button"). FE-1477.
//
// The api's click tracking (queues/cache_mail.py gather_links) looks for comments containing
// `[if mso]><v:roundrect` and rewrites their first double-quoted href, so the shape must open
// the comment and carry the link itself.

const VML_DASH_STYLES = { dashed: 'dash', dotted: 'dot' }

const OPEN_NON_MSO = '<!--[if !mso]><!-->'
const CLOSE_NON_MSO = '<!--<![endif]-->'

// VML's arcsize is the corner radius as a percentage of the shorter side; 50% is a pill.
export const getArcsize = (radius, width, height) => {
  const shorterSide = Math.min(width, height)
  if (!(radius > 0) || !(shorterSide > 0)) {
    return 0
  }
  return Math.min(50, Math.round((radius / shorterSide) * 100))
}

const escapeText = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

// The tracker copies the href out of the comment as is, so `&` must stay raw (an `&amp;` would
// end up in the tracked URL). Only the characters that would break the attribute or the comment
// are percent-encoded.
const escapeHref = (value) =>
  String(value).replace(/"/g, '%22').replace(/</g, '%3C').replace(/>/g, '%3E')

const escapeAttr = (value) =>
  String(value).replace(/"/g, "'").replace(/[<>]/g, '')

export const buildVmlButton = ({
  href,
  width,
  height,
  arcsize,
  backgroundColor,
  textColor,
  fontSize,
  fontFamily,
  borderWidth,
  borderStyle,
  borderColor,
  text,
}) => {
  const strokeWidth = parseFloat(borderWidth) || 0
  const hasBorder = strokeWidth > 0 && !!borderStyle && borderStyle !== 'none'
  const stroke = hasBorder
    ? `strokecolor="${escapeAttr(borderColor)}" strokeweight="${strokeWidth}px"`
    : 'stroke="f"'
  const dashStyle = hasBorder && VML_DASH_STYLES[borderStyle]
  const textStyle = [
    `color:${escapeAttr(textColor)}`,
    fontFamily && `font-family:${escapeAttr(fontFamily)}`,
    `font-size:${parseFloat(fontSize) || 14}px`,
    'font-weight:bold',
  ]
    .filter(Boolean)
    .join(';')

  return (
    '<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word"' +
    ` href="${escapeHref(href)}"` +
    ` style="height:${height}px;v-text-anchor:middle;width:${width}px;"` +
    ` arcsize="${arcsize}%" ${stroke} fillcolor="${escapeAttr(
      backgroundColor
    )}">` +
    (dashStyle ? `<v:stroke dashstyle="${dashStyle}"/>` : '') +
    '<w:anchorlock/>' +
    `<center style="${textStyle};">${
      text ? escapeText(text) : '&nbsp;'
    }</center>` +
    '</v:roundrect>'
  )
}

// The props for a Button or ConfirmButton block: each setting falls back to the editor's global
// button setting, as the blocks' own HTML does. `width`, `height`, `href` and `text` are the
// values the block already resolved for its HTML button.
export const getOutlookButtonProps = ({
  settings,
  globalSettings,
  width,
  height,
  href,
  text,
}) => ({
  radius: settings.borderRadius || globalSettings.button_borderRadius,
  width,
  height,
  href,
  text,
  backgroundColor:
    settings.backgroundColor || globalSettings.button_backgroundColor,
  textColor: settings.textColor || globalSettings.button_textColor,
  fontSize: settings.fontSize || globalSettings.button_fontSize,
  fontFamily: globalSettings.normalText_fontFamily,
  borderWidth: settings.borderWidth || globalSettings.button_borderWidth,
  borderStyle: settings.borderStyle || globalSettings.button_borderStyle,
  borderColor: settings.borderColor || globalSettings.button_borderColor,
})

// Wraps a block's HTML button. Only the server render (the mail markup) gets the Outlook
// version; the editor renders `children` unchanged. A square button already looks right in
// Outlook, so it is left as is.
export const OutlookButton = ({
  serverRender,
  radius,
  width,
  height,
  children,
  ...button
}) => {
  const arcsize = getArcsize(parseFloat(radius), width, height)
  if (!serverRender || !arcsize) {
    return children
  }

  return (
    <>
      <IfIE
        serverRender
        comment={buildVmlButton({ ...button, width, height, arcsize })}
      />
      <i dangerouslySetInnerHTML={{ __html: OPEN_NON_MSO }} />
      {children}
      <i dangerouslySetInnerHTML={{ __html: CLOSE_NON_MSO }} />
    </>
  )
}

OutlookButton.propTypes = {
  serverRender: PropTypes.bool,
  radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  href: PropTypes.string,
  text: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontFamily: PropTypes.string,
  borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderStyle: PropTypes.string,
  borderColor: PropTypes.string,
  children: PropTypes.node,
}

export default OutlookButton
